import { createBrowserRouter, RouterProvider, useNavigate } from 'react-router-dom';
import { Suspense, lazy, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LazyPostHogProvider } from './lib/LazyPostHogProvider';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PremiumRoute } from './components/PremiumRoute';
import { ThemeProvider, LanguageProvider, AuthProvider } from './contexts';
import { PremiumProvider } from './contexts/PremiumContext';
import { ErrorBoundary, RouteErrorBoundary, ReloadPrompt, Paywall } from './components/common';
import { isNativePlatform } from './utils/platform';

/**
 * Wraps a dynamic import with retry logic for stale chunk errors.
 * After a new deployment, users with the app still open may try to load
 * chunks that no longer exist. This detects the failure and reloads the page
 * once to fetch the latest assets.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry<T extends Record<string, any>>(
  importFn: () => Promise<T>,
  namedExport: keyof T,
): React.LazyExoticComponent<React.ComponentType> {
  return lazy(() =>
    importFn()
      .then(m => ({ default: m[namedExport] }))
      .catch((error: unknown) => {
        // Only reload once per session to avoid infinite reload loops
        const storageKey = 'chunk_reload_attempted';
        const alreadyReloaded = sessionStorage.getItem(storageKey);

        if (!alreadyReloaded) {
          sessionStorage.setItem(storageKey, '1');
          window.location.reload();
          // Return a never-resolving promise so React doesn't try to render
          // while the page is reloading
          return new Promise<never>(() => {});
        }

        // If we already reloaded and it still fails, surface the original error
        sessionStorage.removeItem(storageKey);
        throw error;
      }),
  );
}

const _isNative = isNativePlatform();

// On native, eagerly import HomePage so it's available by the time React mounts.
// The dynamic import fires at module evaluation time (before createRoot), and on
// native all code is on local disk so it resolves in <2ms — well before the first
// React render. We wrap it in React.lazy so it integrates with Suspense, but
// because the promise is already resolved, Suspense never shows its fallback.
const _homeImportPromise = _isNative ? import('./pages/HomePage') : null;
const NativeHomePage = _isNative
  ? lazy(() => _homeImportPromise!.then(m => ({ default: m.HomePage })))
  : null;

// Lazy load pages for code splitting (with chunk-load retry)
const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'), 'LandingPage');
const LazyHomePage = lazyWithRetry(() => import('./pages/HomePage'), 'HomePage');
const WorldAroundMeSelector = lazyWithRetry(() => import('./pages/WorldAroundMeSelector'), 'WorldAroundMeSelector');
const SoundLabSelector = lazyWithRetry(() => import('./pages/SoundLabSelector'), 'SoundLabSelector');
const SyllableSelector = lazyWithRetry(() => import('./pages/SyllableSelector'), 'SyllableSelector');
const StickerBook = lazyWithRetry(() => import('./pages/StickerBook'), 'StickerBook');
const ParentArea = lazyWithRetry(() => import('./pages/ParentArea'), 'ParentArea');
const DesignSystem = lazyWithRetry(() => import('./pages/DesignSystem'), 'DesignSystem');
const PracticeGame = lazyWithRetry(() => import('./components/games'), 'PracticeGame');
const SmartMixGame = lazyWithRetry(() => import('./components/games'), 'SmartMixGame');
const QAPage = import.meta.env.DEV
  ? lazyWithRetry(() => import('./pages/QAPage'), 'QAPage')
  : null;

// Loading fallback — matches the HTML splash gradient for a seamless transition
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(to bottom, rgb(233,213,255), rgb(243,232,255), rgb(253,242,248))' }}>
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent" />
    </div>
  );
}

// Helper to wrap components with ProtectedRoute and Suspense
const protect = (element: React.ReactNode) => (
  <ProtectedRoute>
    <Suspense fallback={<PageLoader />}>
      {element}
    </Suspense>
  </ProtectedRoute>
);

// Helper for public routes with Suspense
const withSuspense = (element: React.ReactNode) => (
  <Suspense fallback={<PageLoader />}>
    {element}
  </Suspense>
);

// Helper to wrap premium-only routes with ProtectedRoute + PremiumRoute + Suspense
const premiumProtect = (element: React.ReactNode) => (
  <ProtectedRoute>
    <PremiumRoute>
      <Suspense fallback={<PageLoader />}>
        {element}
      </Suspense>
    </PremiumRoute>
  </ProtectedRoute>
);

// 404 page for unknown routes
const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleGoHome = useCallback(() => {
    navigate('/app', { replace: true });
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-gradient-to-b from-sky-50 to-purple-50 dark:from-dark-bg dark:to-slate-900">
      <div className="text-center max-w-md">
        <div className="text-6xl mb-4">🔍</div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-dark-text mb-2 font-heebo">
          {t('errors.pageNotFound')}
        </h1>
        <p className="text-gray-600 dark:text-dark-muted mb-6 font-heebo">
          {t('errors.pageNotFoundDescription')}
        </p>
        <button
          onClick={handleGoHome}
          className="px-6 py-3 bg-kid-purple hover:bg-purple-600 text-white rounded-xl font-bold shadow-lg transition-colors font-heebo"
        >
          {t('errors.goHome')}
        </button>
      </div>
    </div>
  );
};

// Pick the right HomePage component for the platform.
// On native: NativeHomePage (eager import, no Suspense fallback flash)
// On web: LazyHomePage wrapped with auth protection
const HomePageElement = _isNative
  ? <Suspense fallback={<PageLoader />}>{NativeHomePage && <NativeHomePage />}</Suspense>
  : protect(<LazyHomePage />);

// Modern data router configuration (React Router 7)
const router = createBrowserRouter([
  {
    // Root route provides a shared errorElement for all children
    errorElement: <RouteErrorBoundary />,
    children: [
      // On native (iOS/Android), render HomePage directly at `/` — no redirect,
      // no extra render cycle. The eager import resolves before React mounts so
      // Suspense never shows its fallback. On web, show the marketing landing page.
      {
        path: '/',
        element: _isNative
          ? HomePageElement
          : withSuspense(<LandingPage />),
      },

      // /app route — on native this is the same HomePage (for internal navigation),
      // on web it's behind the auth gate.
      {
        path: '/app',
        element: _isNative
          ? HomePageElement
          : protect(<LazyHomePage />),
      },
      
      // Practice Category Selectors (premium game modes)
      {
        path: '/practice/world',
        element: premiumProtect(<WorldAroundMeSelector />),
      },
      {
        path: '/practice/sound-lab',
        element: premiumProtect(<SoundLabSelector />),
      },
      {
        path: '/practice/sound-lab/:group',
        element: premiumProtect(<SoundLabSelector />),
      },
      {
        path: '/practice/sound-lab/:group/:letter',
        element: premiumProtect(<SoundLabSelector />),
      },
      {
        path: '/practice/syllables',
        element: premiumProtect(<SyllableSelector />),
      },
      
      // Other pages
      {
        path: '/stickers',
        element: protect(<StickerBook />),
      },
      {
        path: '/parent',
        element: protect(<ParentArea />),
      },
      {
        path: '/design-system',
        element: withSuspense(<DesignSystem />),
      },
      ...(QAPage ? [{
        path: '/qa',
        element: withSuspense(<QAPage />),
      }] : []),
      
      // Game routes - URL-based routing for different category types
      {
        path: '/game/world/:location',
        element: premiumProtect(<PracticeGame />),
      },
      {
        path: '/game/sound-lab/:group/:letter',
        element: premiumProtect(<PracticeGame />),
      },
      {
        path: '/game/syllables/:count',
        element: premiumProtect(<PracticeGame />),
      },
      {
        path: '/game/smart-mix',
        element: _isNative
          ? withSuspense(<SmartMixGame />)
          : protect(<SmartMixGame />),
      },
      {
        path: '/game',
        element: protect(<PracticeGame />),
      },

      // Catch-all 404 route
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);

/**
 * Removes the inline HTML splash screen (from index.html) with a fade-out
 * so there's no jarring flash when React takes over rendering.
 * Called from App mount — on native the eager-imported HomePage resolves
 * synchronously so the splash hides as soon as real content is painted.
 * On web the Suspense fallback (PageLoader) renders behind the splash,
 * then the splash fades out revealing the loader, which is identical visually.
 */
function useDismissSplash() {
  useEffect(() => {
    const splash = document.getElementById('splash');
    if (!splash) return;

    // Wait one frame so React's first real paint is committed to the screen,
    // then fade out the splash overlay to reveal the rendered content beneath.
    requestAnimationFrame(() => {
      splash.style.transition = 'opacity 120ms ease-out';
      splash.style.opacity = '0';
      setTimeout(() => {
        splash.remove();
      }, 130);
    });
  }, []);
}

function App() {
  useDismissSplash();

  return (
    <ErrorBoundary>
      <LazyPostHogProvider>
        <LanguageProvider>
          <ThemeProvider>
            <AuthProvider>
              <PremiumProvider>
                <RouterProvider router={router} />
                <Paywall />
                <ReloadPrompt />
              </PremiumProvider>
            </AuthProvider>
          </ThemeProvider>
        </LanguageProvider>
      </LazyPostHogProvider>
    </ErrorBoundary>
  );
}

export default App;
